
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import PrecisionManufacturingRoundedIcon from "@mui/icons-material/PrecisionManufacturingRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import {
  getErrorMessage,
  getRequest,
  MANUFACTURING_API,
  patchRequest,
  postRequest,
} from "./api";
import {
  PageShell,
  SectionCard,
  SummaryCard,
  panelSx,
  statusChip,
} from "./ManufacturingCommon";

const STAGE_OPTIONS = [
  "ASSEMBLED",
  "FLASHED",
  "TESTING",
  "REWORK",
  "PACKED",
  "DISPATCHED",
];
const TEST_OPTIONS = ["PASS", "FAIL", "PARTIAL", "SKIPPED"];
const emptyEditPayload = {
  base_os_version: "",
  firmware_version: "",
  mac_address: "",
  imei: "",
  bluetooth_mac: "",
  wifi_mac: "",
  notes: "",
};

function UnitDetailSkeleton() {
  const summaryCards = Array.from({ length: 3 });
  const summaryRows = Array.from({ length: 7 });
  const timelineRows = Array.from({ length: 5 });
  const testCards = Array.from({ length: 3 });

  return (
    <PageShell title="Manufacturing Unit" subtitle="Loading unit traceability details">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(3, 1fr)",
          },
          gap: 2,
          width: "100%",
        }}
      >
        {summaryCards.map((_, index) => (
          <Box key={index} sx={{ ...panelSx, p: 2.2, minHeight: 150 }}>
            <Skeleton variant="text" width="44%" height={18} />
            <Skeleton variant="text" width="56%" height={34} sx={{ mt: 1 }} />
            <Skeleton variant="text" width="72%" height={22} sx={{ mt: 1 }} />
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "repeat(12, 1fr)",
          },
          gap: 2,
          mt: 2,
        }}
      >
        <Box sx={{ gridColumn: { xs: "span 12", lg: "span 4" } }}>
          <Box sx={{ ...panelSx, p: 2.2 }}>
            <Skeleton variant="text" width="38%" height={30} />
            <Skeleton variant="text" width="74%" height={22} />
            <Stack spacing={1.2} sx={{ mt: 2 }}>
              {summaryRows.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1.35,
                    borderRadius: 3,
                    backgroundColor: "#fff",
                    border: "1px solid rgba(226,232,240,0.9)",
                  }}
                >
                  <Skeleton variant="text" width="42%" height={20} />
                  <Skeleton variant="text" width="68%" height={24} sx={{ mt: 0.55 }} />
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>

        <Box sx={{ gridColumn: { xs: "span 12", lg: "span 8" } }}>
          <Box sx={{ ...panelSx, p: 2.2 }}>
            <Skeleton variant="text" width="28%" height={30} />
            <Skeleton variant="text" width="68%" height={22} />
            <Stack spacing={1.2} sx={{ mt: 2 }}>
              {timelineRows.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1.35,
                    borderRadius: 3,
                    backgroundColor: "#fff",
                    border: "1px solid rgba(226,232,240,0.9)",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" gap={2}>
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="44%" height={24} />
                      <Skeleton variant="text" width="72%" height={20} />
                    </Box>
                    <Skeleton variant="rounded" width={92} height={26} sx={{ borderRadius: 999 }} />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>

        <Box sx={{ gridColumn: "span 12" }}>
          <Box sx={{ ...panelSx, p: 2.2 }}>
            <Skeleton variant="text" width="28%" height={30} />
            <Skeleton variant="text" width="62%" height={22} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, 1fr)",
                  xl: "repeat(3, 1fr)",
                },
                gap: 2,
                mt: 2,
              }}
            >
              {testCards.map((_, index) => (
                <Box key={index} sx={{ ...panelSx, p: 1.6, boxShadow: "none" }}>
                  <Skeleton variant="text" width="48%" height={24} />
                  <Skeleton variant="text" width="70%" height={20} />
                  <Skeleton variant="text" width="58%" height={20} />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </PageShell>
  );
}

export default function ManufacturingUnitDetail() {
  const { id } = useParams();
  const { activeOrg } = useAuth();
  const token = localStorage.getItem("authToken");
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [openEdit, setOpenEdit] = useState(false);
  const [openStage, setOpenStage] = useState(false);
  const [openTest, setOpenTest] = useState(false);
  const [editPayload, setEditPayload] = useState(emptyEditPayload);
  const [editErrors, setEditErrors] = useState({
  base_os_version: "",
});
  const [stagePayload, setStagePayload] = useState({
    status: "ASSEMBLED",
    stage: "ASSEMBLED",
    base_os_version: "",
  });
  const [testPayload, setTestPayload] = useState({
    result: "PASS",
    test_plan_version: "",
    firmware_version: "",
    failure_code: "",
    failure_summary: "",
  });
  const [stageLoading, setStageLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [timelinePage, setTimelinePage] = useState(1);
  const [testRunsPage, setTestRunsPage] = useState(1);

  const timelinePerPage = 7;
  const testRunsPerPage = 6;

  const loadUnit = useCallback(async () => {
    if (!token || !activeOrg?.id || !id) return;
    setLoading(true);
    setError("");
    try {
      const data = await getRequest(
        `${MANUFACTURING_API}/units/${id}`,
        token,
        activeOrg,
      );
      setUnit(data);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load manufacturing unit"));
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg, id]);

  useEffect(() => {
    loadUnit();
  }, [loadUnit]);

  useEffect(() => {
    if (!unit) return;
    setEditPayload({
      base_os_version: unit.base_os_version || "",
      firmware_version: unit.firmware_version || "",
      mac_address: unit.mac_address || "",
      imei: unit.imei || "",
      bluetooth_mac: unit.bluetooth_mac || "",
      wifi_mac: unit.wifi_mac || "",
      notes: unit.notes || "",
    });
  }, [unit]);

  const doAction = async (fn, successMessage, errorMessage = "Action failed") => {
    try {
      await fn();
      setSuccess(successMessage);
      await loadUnit();
    } catch (err) {
      setError(getErrorMessage(err, errorMessage));
    }
  };

  const timelineEvents = unit?.events || [];
  const testRuns = unit?.test_runs || [];

  const paginatedTimelineEvents = useMemo(() => {
    const startIndex = (timelinePage - 1) * timelinePerPage;
    return timelineEvents.slice(startIndex, startIndex + timelinePerPage);
  }, [timelineEvents, timelinePage]);

  const paginatedTestRuns = useMemo(() => {
    const startIndex = (testRunsPage - 1) * testRunsPerPage;
    return testRuns.slice(startIndex, startIndex + testRunsPerPage);
  }, [testRuns, testRunsPage]);

  useEffect(() => {
    const maxTimelinePage = Math.max(
      1,
      Math.ceil(timelineEvents.length / timelinePerPage),
    );
    if (timelinePage > maxTimelinePage) {
      setTimelinePage(maxTimelinePage);
    }
  }, [timelineEvents.length, timelinePage]);

  useEffect(() => {
    const maxTestRunsPage = Math.max(
      1,
      Math.ceil(testRuns.length / testRunsPerPage),
    );
    if (testRunsPage > maxTestRunsPage) {
      setTestRunsPage(maxTestRunsPage);
    }
  }, [testRuns.length, testRunsPage]);
useEffect(() => {
  if (openEdit && unit) {
    setEditPayload({
      base_os_version: unit.base_os_version || "",
      firmware_version: unit.firmware_version || "",
      mac_address: unit.mac_address || "",
      imei: unit.imei || "",
      bluetooth_mac: unit.bluetooth_mac || "",
      wifi_mac: unit.wifi_mac || "",
      notes: unit.notes || "",
    });
  }
}, [openEdit, unit]);
  if (loading) {
    return <UnitDetailSkeleton />;
  }

  if (!unit) {
    return (
      <PageShell title="Manufacturing Unit" subtitle="Unit detail">
        <Alert severity="error">Manufacturing unit not found</Alert>
      </PageShell>
    );
  }

  const summaryRows = [
    ["Base OS", unit.base_os_version || "-"],
    ["Firmware", unit.firmware_version || "-"],
    ["MAC", unit.mac_address || "-"],
    ["IMEI", unit.imei || "-"],
    ["Bluetooth MAC", unit.bluetooth_mac || "-"],
    ["Wi-Fi MAC", unit.wifi_mac || "-"],
    ["Rework Count", unit.rework_count || 0],
    ["Inventory Asset", unit.inventory_asset_id || "-"],
  ];

const handleCloseEdit = () => {
  setOpenEdit(false);

  // reset errors when dialog closes
  setEditErrors({
    base_os_version: "",
    
  });
  setEditPayload(emptyEditPayload);
};
  return (
    <PageShell
      title={unit.serial_number}
      subtitle="Manufacturing unit detail, traceability, quality history, and inventory conversion."
      actions={
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<EditRoundedIcon />}
              onClick={() => setOpenEdit(true)}
              sx={{ color: "#F8FAFC", borderColor: "rgba(255,255,255,0.18)" }}
            >
              Edit Unit
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrecisionManufacturingRoundedIcon />}
              onClick={() => setOpenStage(true)}
              sx={{ color: "#F8FAFC", borderColor: "rgba(255,255,255,0.18)" }}
            >
              Record Stage
            </Button>
            <Button
              variant="outlined"
              startIcon={<FactCheckRoundedIcon />}
              onClick={() => setOpenTest(true)}
              sx={{ color: "#F8FAFC", borderColor: "rgba(255,255,255,0.18)" }}
            >
              Record Test
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button
              variant="outlined"
              onClick={() =>
                doAction(
                  () =>
                    postRequest(
                      `${MANUFACTURING_API}/units/${id}/approve`,
                      {},
                      token,
                      activeOrg,
                    ),
                  "Unit approved",
                  "Unable to approve unit",
                )
              }
              sx={{ color: "#F8FAFC", borderColor: "rgba(255,255,255,0.18)" }}
            >
              Approve
            </Button>
            <Button
              color="warning"
              variant="outlined"
              onClick={() =>
                doAction(
                  () =>
                    postRequest(
                      `${MANUFACTURING_API}/units/${id}/scrap`,
                      { reason: "Manual scrap action" },
                      token,
                      activeOrg,
                    ),
                  "Unit scrapped",
                  "Unable to scrap unit",
                )
              }
              sx={{ borderColor: "rgba(255,255,255,0.18)" }}
            >
              Scrap
            </Button>
            <Button
              variant="contained"
              startIcon={<Inventory2RoundedIcon />}
              onClick={() =>
                doAction(
                  () =>
                    postRequest(
                      `${MANUFACTURING_API}/units/${id}/convert-to-asset`,
                      {},
                      token,
                      activeOrg,
                    ),
                  "Unit converted to inventory asset",
                  "Unable to convert unit to inventory asset",
                )
              }
              sx={{
                backgroundColor: "#F8FAFC",
                color: "#0F172A",
                "&:hover": { backgroundColor: "#E2E8F0" },
              }}
            >
              Convert To Asset
            </Button>
          </Stack>
        </Stack>
      }
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}

      <Box
        sx={{
          mb: 3,
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(3, 1fr)",
          },
          gap: 2,
          width: "100%",
        }}
      >
        <SummaryCard
          title="Status"
          value={statusChip(unit.status)}
          subtitle="Current unit lifecycle stage"
          eyebrow="Execution State"
        />

        <SummaryCard
          title="QC Result"
          value={unit.qc_result ? statusChip(unit.qc_result) : "-"}
          subtitle="Most recent test outcome"
          eyebrow="Quality"
        />

        <SummaryCard
          title="Rework Count"
          value={unit.rework_count || 0}
          subtitle="Number of rework loops logged"
          eyebrow="Recovery"
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "repeat(12, 1fr)",
          },
          gap: 2,
        }}
      >
        <Box sx={{ gridColumn: { xs: "span 12", lg: "span 4" } }}>
          <SectionCard
            title="Unit Summary"
            subtitle="Primary traceability and hardware identity details."
          >
            <Stack spacing={1.2}>
              {summaryRows.map(([label, value]) => (
                <Box
                  key={label}
                  sx={{
                    p: 1.35,
                    borderRadius: 3,
                    backgroundColor: "#fff",
                    border: "1px solid rgba(226,232,240,0.9)",
                  }}
                >
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.55,
                      fontSize: 14,
                      fontWeight: 800,
                      color: "#0F172A",
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </SectionCard>
        </Box>

        <Box sx={{ gridColumn: { xs: "span 12", lg: "span 8" } }}>
          <SectionCard
            title="Timeline"
            subtitle="Recorded stage transitions and manufacturing execution history."
          >
            {timelineEvents.length ? (
              <Stack spacing={1.2}>
                {paginatedTimelineEvents.map((event) => (
                  <Box
                    key={event.id}
                    sx={{
                      p: 1.35,
                      borderRadius: 3,
                      backgroundColor: "#fff",
                      border: "1px solid rgba(226,232,240,0.9)",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      gap={2}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>
                          {event.event_type.replace(/_/g, " ")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {event.stage || "Stage"} |{" "}
                          {event.created_at
                            ? new Date(event.created_at).toLocaleString()
                            : "-"}
                        </Typography>
                      </Box>
                      {event.status_after ? statusChip(event.status_after) : null}
                    </Stack>
                  </Box>
                ))}
                {timelineEvents.length > timelinePerPage ? (
                  <Pagination
                    page={timelinePage}
                    count={Math.ceil(timelineEvents.length / timelinePerPage)}
                    onChange={(_, page) => setTimelinePage(page)}
                    siblingCount={0}
                    boundaryCount={1}
                    color="primary"
                    shape="rounded"
                    sx={{ alignSelf: "center", pt: 0.5 }}
                  />
                ) : null}
              </Stack>
            ) : (
              <Typography color="text.secondary">
                No unit events recorded yet.
              </Typography>
            )}
          </SectionCard>
        </Box>

        <Box sx={{ gridColumn: "span 12" }}>
          <SectionCard
            title="Test Runs"
            subtitle="Quality records and observed failure summaries for this unit."
          >
            {testRuns.length ? (
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(2, 1fr)",
                      xl: "repeat(3, 1fr)",
                    },
                    gap: 2,
                  }}
                >
                  {paginatedTestRuns.map((test) => (
                    <Box
                      key={test.id}
                      sx={{
                        ...panelSx,
                        p: 1.6,
                        boxShadow: "none",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        gap={2}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>
                            {test.test_plan_version || "Test run"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {test.executed_at
                              ? new Date(test.executed_at).toLocaleString()
                              : "-"}
                          </Typography>
                        </Box>
                        {statusChip(test.result)}
                      </Stack>
                      <Typography
                        sx={{
                          mt: 1,
                          fontSize: 13,
                          color: "#64748B",
                          lineHeight: 1.7,
                        }}
                      >
                        {test.failure_summary || "No failure summary recorded."}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {testRuns.length > testRunsPerPage ? (
                  <Pagination
                    page={testRunsPage}
                    count={Math.ceil(testRuns.length / testRunsPerPage)}
                    onChange={(_, page) => setTestRunsPage(page)}
                    siblingCount={0}
                    boundaryCount={1}
                    color="primary"
                    shape="rounded"
                    sx={{ alignSelf: "center" }}
                  />
                ) : null}
              </Stack>
            ) : (
              <Typography color="text.secondary">No test runs recorded yet.</Typography>
            )}
          </SectionCard>
        </Box>
      </Box>

      <Dialog
        open={openStage}
        onClose={() => setOpenStage(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: panelSx }}
      >
       <DialogTitle
  sx={{
    background:
      "linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,64,175,0.92) 58%, rgba(13,148,136,0.86))",
    color: "#F8FAFC",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  }}
>
  Record Manufacturing Stage

  <IconButton
    onClick={() => setOpenStage(false)}
    sx={{ color: "#F8FAFC" }}
  >
    <CloseIcon />
  </IconButton>
</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              select
              fullWidth
              label="Status"
              value={stagePayload.status}
              onChange={(e) =>
                setStagePayload((p) => ({
                  ...p,
                  status: e.target.value,
                  stage: e.target.value,
                }))
              }
            >
              {STAGE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Base OS Version"
              value={stagePayload.base_os_version}
              onChange={(e) =>
                setStagePayload((p) => ({
                  ...p,
                  base_os_version: e.target.value,
                }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenStage(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={stageLoading}
            onClick={async () => {
              setStageLoading(true);
              try {
                await doAction(
                  () =>
                    postRequest(
                      `${MANUFACTURING_API}/units/${id}/record-stage`,
                      stagePayload,
                      token,
                      activeOrg,
                    ),
                  "Stage recorded",
                  "Unable to record manufacturing stage",
                );
                setOpenStage(false);
              } finally {
                setStageLoading(false);
              }
            }}
          >
            {stageLoading ? <CircularProgress size={16} /> : "Save Stage"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEdit}
        onClose={handleCloseEdit}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: panelSx }}
      >
       <DialogTitle
  sx={{
    background:
      "linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,64,175,0.92) 58%, rgba(13,148,136,0.86))",
    color: "#F8FAFC",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  }}
>
  Edit Unit Details

  <IconButton
    onClick={handleCloseEdit}
    sx={{ color: "#F8FAFC" }}
  >
    <CloseIcon />
  </IconButton>
</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
  fullWidth
  required
  label="Base OS Version"
  value={editPayload.base_os_version}
  error={!!editErrors.base_os_version}
  helperText={editErrors.base_os_version}
  onChange={(e) => {
    const value = e.target.value;

    setEditPayload((p) => ({ ...p, base_os_version: value }));

    // remove error on typing
    if (value.trim()) {
      setEditErrors((prev) => ({ ...prev, base_os_version: "" }));
    }
  }}
/>
            <TextField
              fullWidth
              label="Firmware Version"
              value={editPayload.firmware_version}
              onChange={(e) =>
                setEditPayload((p) => ({ ...p, firmware_version: e.target.value }))
              }
            />
            <TextField
              fullWidth
              label="MAC Address"
              value={editPayload.mac_address}
              onChange={(e) =>
                setEditPayload((p) => ({ ...p, mac_address: e.target.value }))
              }
            />
            <TextField
              fullWidth
              label="IMEI"
              value={editPayload.imei}
              onChange={(e) => setEditPayload((p) => ({ ...p, imei: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Bluetooth MAC"
              value={editPayload.bluetooth_mac}
              onChange={(e) =>
                setEditPayload((p) => ({ ...p, bluetooth_mac: e.target.value }))
              }
            />
            <TextField
              fullWidth
              label="Wi-Fi MAC"
              value={editPayload.wifi_mac}
              onChange={(e) =>
                setEditPayload((p) => ({ ...p, wifi_mac: e.target.value }))
              }
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Notes"
              value={editPayload.notes}
              onChange={(e) => setEditPayload((p) => ({ ...p, notes: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button
            variant="contained"
            disabled={editLoading}
           onClick={async () => {
  const errors = {};

  if (!editPayload.base_os_version?.trim()) {
    errors.base_os_version = "Required";
  }

  setEditErrors(errors);

  // stop if error exists
  if (Object.keys(errors).length > 0) return;

  setEditLoading(true);
  try {
    await doAction(
      () =>
        patchRequest(
          `${MANUFACTURING_API}/units/${id}`,
          editPayload,
          token,
          activeOrg,
        ),
      "Unit updated",
      "Unable to update unit",
    );
    handleCloseEdit(); // ✅ also clears errors
  } finally {
    setEditLoading(false);
  }
}}
          >
            {editLoading ? <CircularProgress size={16} color="inherit" /> : "Save Unit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openTest}
        onClose={() => setOpenTest(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: panelSx }}
      >
        <DialogTitle
          sx={{
            background:
              "linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,64,175,0.92) 58%, rgba(13,148,136,0.86))",
            color: "#F8FAFC",
             display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
          }}
        >
          Record Test Run
           <IconButton
    onClick={() => setOpenTest(false)}
    sx={{ color: "#F8FAFC" }}
  >
    <CloseIcon />
  </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              select
              fullWidth
              label="Result"
              value={testPayload.result}
              onChange={(e) => setTestPayload((p) => ({ ...p, result: e.target.value }))}
            >
              {TEST_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Test Plan Version"
              value={testPayload.test_plan_version}
              onChange={(e) =>
                setTestPayload((p) => ({ ...p, test_plan_version: e.target.value }))
              }
            />
            <TextField
              fullWidth
              label="Firmware Version"
              value={testPayload.firmware_version}
              onChange={(e) =>
                setTestPayload((p) => ({ ...p, firmware_version: e.target.value }))
              }
            />
            <TextField
              fullWidth
              label="Failure Code"
              value={testPayload.failure_code}
              onChange={(e) =>
                setTestPayload((p) => ({ ...p, failure_code: e.target.value }))
              }
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Failure Summary"
              value={testPayload.failure_summary}
              onChange={(e) =>
                setTestPayload((p) => ({ ...p, failure_summary: e.target.value }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenTest(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={testLoading}
            onClick={async () => {
              setTestLoading(true);
              try {
                await doAction(
                  () =>
                    postRequest(
                      `${MANUFACTURING_API}/units/${id}/record-test`,
                      testPayload,
                      token,
                      activeOrg,
                    ),
                  "Test run recorded",
                  "Unable to record test run",
                );
                setOpenTest(false);
              } finally {
                setTestLoading(false);
              }
            }}
          >
            {testLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              "Save Test"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}

